import { DragEscapeGuard } from 'src/modules/shared/components/atoms/drag-escape-guard';
import { useMemo, useRef, useCallback, useState, type ReactNode } from 'react';
import {
  DndContext,
  type DragEndEvent,
  type DragStartEvent,
  DragOverlay,
  type SensorDescriptor,
  type SensorOptions,
  defaultDropAnimationSideEffects,
  rectIntersection,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  DraggableItem,
  type DraggableChildrenProps,
} from 'src/modules/shared/components/molecules/draggable-item';
import { type BaseProps } from 'src/modules/shared/types/component.type';

const dropAnimation = {
  duration: 200,
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.5',
      },
    },
  }),
};

interface DraggableColumnListProps<TItem> extends BaseProps {
  /** Array of items to render as columns */
  items: TItem[];
  /** Callback to render the content of each item */
  renderItem: (item: TItem, dragProps: DraggableChildrenProps) => ReactNode;
  /** Function to get a unique ID from each item */
  getItemId: (item: TItem) => string | number;
  /** Whether items can be reordered */
  canReorder?: boolean;
  /** Optional per-item guard for protected columns. */
  isItemReorderable?: (item: TItem) => boolean;
  /** Optional drag overlay component to render when dragging */
  dragOverlay?: (activeItem: TItem) => ReactNode;
  /** Optional sensors to use for drag detection */
  sensors?: SensorDescriptor<SensorOptions>[];
  /** Optional callback for drag start event */
  onDragStart?: (event: DragStartEvent) => void;
  /** Optional custom drag end handler */
  onDragEnd?: (event: DragEndEvent) => void;
  /** Optional active item being dragged */
  activeItem?: TItem | null;
  /** Whether animations should be enabled */
  shouldAnimate?: boolean;
  /** Optional CSS class for column width */
  columnClassName?: string;
}

export const DraggableColumnList = <TItem,>({
  items,
  renderItem,
  getItemId,
  canReorder = true,
  isItemReorderable,
  dragOverlay,
  className = '',
  columnClassName = 'min-w-[230px] max-w-[280px]',
  sensors,
  onDragStart,
  onDragEnd,
  activeItem,
  shouldAnimate = true,
}: DraggableColumnListProps<TItem>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showLeftBorder, setShowLeftBorder] = useState(false);
  const [showRightBorder, setShowRightBorder] = useState(false);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollLeft, clientWidth, scrollWidth } = e.currentTarget;
    // Show left border when not at start
    setShowLeftBorder(scrollLeft > 0);
    // Show right border when not at end
    setShowRightBorder(scrollLeft + clientWidth < scrollWidth - 10);
  }, []);

  const itemIds = useMemo(
    () => items.map((item) => `item-${getItemId(item)}`),
    [items, getItemId]
  );

  return (
    <DndContext
      collisionDetection={rectIntersection}
      onDragEnd={onDragEnd}
      onDragStart={onDragStart}
      sensors={sensors}
    >
      <DragEscapeGuard />
      <SortableContext items={itemIds} strategy={horizontalListSortingStrategy}>
        <div
          ref={containerRef}
          className={`overflow-x-auto pb-4 ${className}`}
          onScroll={handleScroll}
          style={{
            borderLeft: showLeftBorder ? '1px solid #d1d1d8' : 'none',
            borderRight: showRightBorder ? '1px solid #d1d1d8' : 'none',
          }}
        >
          <div className="flex gap-3 flex-nowrap w-max">
            {items.map((item) => (
              <DraggableItem
                key={getItemId(item)}
                id={`item-${getItemId(item)}`}
                data={item}
                disabled={!canReorder || isItemReorderable?.(item) === false}
                className={columnClassName}
                shouldAnimate={shouldAnimate}
              >
                {(dragProps) => renderItem(item, dragProps)}
              </DraggableItem>
            ))}
          </div>
        </div>
      </SortableContext>

      {dragOverlay && activeItem && (
        <DragOverlay
          adjustScale={true}
          dropAnimation={shouldAnimate ? dropAnimation : null}
        >
          <div
            className="transform-gpu will-change-transform opacity-80 grayscale"
            style={{
              cursor: 'grabbing',
              touchAction: 'none',
              pointerEvents: 'none',
            }}
          >
            {dragOverlay(activeItem)}
          </div>
        </DragOverlay>
      )}
    </DndContext>
  );
};
